import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Grid, MailIcon, Paper, Typography } from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';

import { Footer, NonProdBanner } from '@/components';
import { useAppContext } from '@/context';
import { useFRCallback } from '@/hooks';
import { avLogoWhite, avBackgroundOrange } from '@/static';

const currentEnv = getSpecificEnv();
const shouldShowNonProdBanner = currentEnv !== 'prod';

const NewUserCompleted = ({ ...props }) => {
  const { stepToRender } = props;
  const { setMessages } = useAppContext();
  const { suspendedTextOutputCallback } = useFRCallback(stepToRender);
  const navigate = useNavigate();

  return suspendedTextOutputCallback ? (
    <Grid
      id="new-user-completed-page-grid-container"
      container
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      {shouldShowNonProdBanner ? (
        <Grid
          id="nonprod-banner-grid-container"
          display="flex"
          mx={2}
          mt={2}
          justifyContent="center"
          sx={{ marginBottom: { xs: 2, md: 0 } }}
        >
          <NonProdBanner />
        </Grid>
      ) : (
        <Box />
      )}
      <Grid
        id="new-user-completed-page-content-grid"
        container
        display="flex"
        flexDirection="row"
        justifyContent="center"
        flexWrap="wrap"
        mt="auto"
      >
        <Paper
          variant="elevation"
          elevation={6}
          sx={{
            width: { xs: '350px', sm: '500px', md: '700px' },
            marginRight: { sm: 2 },
            marginBottom: { xs: 2, md: 0 },
          }}
        >
          <CardContent>
            <Grid display="flex" flexDirection="column" sx={{ textAlign: 'center', minHeight: '400px' }}>
              <Box mt="auto">
                <Typography variant="h1" mb={2}>
                  We&apos;ve sent you a verification email.
                </Typography>
                <MailIcon fontSize="xlarge" sx={{ alignSelf: 'center' }} />
                <Typography mb={2} variant="subtitle1" fontWeight="bold">
                  Please check your inbox and confirm your email address.
                </Typography>
                <Typography variant="subtitle1">
                  A verification email has been sent to your inbox. Please locate this email and verify your email
                  address to create your Availity account.
                </Typography>
              </Box>
              <Box mt="auto" />
            </Grid>
          </CardContent>
        </Paper>
        <Card
          variant="elevation"
          elevation={6}
          sx={{
            alignItems: 'stretch',
            backgroundImage: `url(${avBackgroundOrange})`,
            backgroundSize: 'cover',
            backgroundClip: 'border-box',
          }}
        >
          <Grid display="flex" flexDirection="column" p={3} justifyContent="space-between" sx={{ height: '100%' }}>
            <Grid display="flex" mb="auto" justifyContent="end">
              <img
                src={avLogoWhite}
                alt="Availity Logo"
                style={{
                  maxWidth: '60%',
                }}
              />
            </Grid>
            <Grid display="flex" mb={2} justifyContent="center">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  Already have an account?
                </Box>
              </Typography>
            </Grid>
            <Grid mb="auto">
              <Button
                color="secondary"
                onClick={() => {
                  setMessages([]); // clear out messages on route navigation
                  navigate('/login');
                }}
                sx={{ width: '100%' }}
              >
                Log in.
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Footer />
    </Grid>
  ) : null;
};

NewUserCompleted.propTypes = {
  stepToRender: PropTypes.object.isRequired,
};

export default NewUserCompleted;
